import { Plane, Vector3 } from "three";
import SceneObject from "./SceneObject";
import EditHandleData from "../sceneObjectData/EditHandleData";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import CommandIdEnum from "../../enums/CommandIdEnums";
import LengthDimensionData from "../sceneObjectData/LengthDimensionData";
import { isPlaneEdgeOn } from "../../utils/editHandleVisibility";
import PropertyList from "../properties/PropertyList";
import { PropertyId } from "../properties/PropertyId";
import PropertyItem from "../properties/PropertyItem";
import { PID_FONT_SIZE } from "../properties/PropertyIds";
import { CATEGORY_COMMON } from "../properties/PropertyCategoryRegistry";
import lengthCmTranslator from "../properties/translators/lengthCmTranslator";

export default class LengthDimensionSceneObject extends SceneObject<LengthDimensionData> {

    getProperties(props: PropertyList, filterId?: PropertyId): void {
        super.getProperties(props, filterId);

        const fontSize = props.getOrCreateProperty(PID_FONT_SIZE, () => new PropertyItem(PID_FONT_SIZE, lengthCmTranslator));
        fontSize.value = this.data.fontSize;
        fontSize.categoryId = CATEGORY_COMMON.id;
    }

    setProperties(props: PropertyList): void {
        super.setProperties(props);

        const fontSize = props.getProperty<number>(PID_FONT_SIZE);
        if (fontSize) this.data.fontSize = fontSize.value;

        this.geometry.update(this.data, false);
    }

    getEditHandles(localViewDirection: Vector3): EditHandleData[] {
        const data = this.data;
        const dimensionAxis = data.end.clone().sub(data.start).normalize();
        const midpoint = data.start.clone().lerp(data.end, 0.5).add(data.offset);
        const plane = new Plane().setFromCoplanarPoints(
            data.start,
            data.end,
            data.start.clone().add(data.offset)
        );
        const offsetDirection = data.offset.lengthSq() > 1e-10
            ? data.offset.clone().normalize()
            : undefined;
        const planeNormal = plane.normal.clone();

        // Seen edge-on the whole dimension collapses onto a line, so its handles would pile up on
        // top of each other. (A zero offset leaves planeNormal degenerate, which keeps the handles.)
        if (isPlaneEdgeOn(localViewDirection, planeNormal)) return [];

        const originalOffset = data.offset.clone();
        const preserveOffsetDirection = (ld: LengthDimensionData) => {
            const offsetLength = originalOffset.length();
            if (offsetLength < 1e-10) return;
            const newAxis = ld.end.clone().sub(ld.start);
            if (newAxis.lengthSq() < 1e-10) return;
            newAxis.normalize();
            const perp = planeNormal.clone().cross(newAxis);
            if (perp.lengthSq() < 1e-10) return;
            perp.normalize();
            if (perp.dot(originalOffset) < 0) perp.negate();
            ld.offset.copy(perp.multiplyScalar(offsetLength));
        };
        return [
            new EditHandleData(data.uuid, EditHandleTypeEnum.START_POINT, data.start.clone(),
                CommandIdEnum.GeometryDimensionEndpointMove,
                (d, delta) => {
                    const ld = d as LengthDimensionData;
                    ld.start.add(delta);
                    ld.text = ld.computeDisplayText();
                    preserveOffsetDirection(ld);
                },
                plane.clone(),
                dimensionAxis.clone()),
            new EditHandleData(data.uuid, EditHandleTypeEnum.END_POINT, data.end.clone(),
                CommandIdEnum.GeometryDimensionEndpointMove,
                (d, delta) => {
                    const ld = d as LengthDimensionData;
                    ld.end.add(delta);
                    ld.text = ld.computeDisplayText();
                    preserveOffsetDirection(ld);
                },
                plane.clone(),
                dimensionAxis.clone()),
            new EditHandleData(data.uuid, EditHandleTypeEnum.DIMENSION_OFFSET, midpoint,
                CommandIdEnum.GeometryDirectionalMove,
                (d, delta) => (d as LengthDimensionData).offset.add(delta),
                plane.clone(),
                offsetDirection),
        ];
    }
}
